import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { formatBookingLocation, formatPaymentStatusDisplay } from "../shared/bookingHistoryShared";

const MARGIN = 14;
const PAGE_W = 210;
const BRAND = [10, 30, 64];
const BRAND_LIGHT = [226, 232, 240];
const MUTED = [100, 116, 139];
const SUCCESS = [22, 163, 74];

function displayText(v) {
  if (v == null) return "—";
  const t = String(v).trim();
  if (t === "" || t === "null") return "—";
  return t;
}

function formatMoney(amount, currency = "INR") {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const n = Number(amount);
  const formatted = n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (currency === "INR") return `Rs. ${formatted}`;
  return `${currency} ${formatted}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return dayjs(value).format("DD MMM YYYY, hh:mm A");
  } catch {
    return String(value);
  }
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return dayjs(value).format("DD MMM YYYY");
  } catch {
    return String(value);
  }
}

function parseCancellationPolicies(raw) {
  if (!raw || typeof raw !== "string") return [];
  const s = raw.trim();
  if (s === "" || s === "null") return [];
  try {
    const arr = JSON.parse(s);
    const rules = [];
    for (const block of Array.isArray(arr) ? arr : []) {
      for (const r of block?.rules || []) {
        rules.push(r);
      }
    }
    return rules;
  } catch {
    return [];
  }
}

/**
 * Load public Wordmark SVG, rasterize to PNG data URL for jsPDF.
 */
async function loadWordmarkPngDataUrl() {
  const base = process.env.PUBLIC_URL || "";
  const path = `${base}/images/Wordmark.svg`.replace(/([^:]\/)\/+/g, "$1");
  const res = await fetch(path);
  if (!res.ok) throw new Error("Logo not found");
  const svgText = await res.text();
  const blob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    const targetW = 600;
    const ratio = 646.57 / 3342.73;
    const targetH = Math.max(60, Math.round(targetW * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unsupported");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(img, 0, 0, targetW, targetH);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

function safeFileName(orderNumber, bookingId) {
  const raw = orderNumber || `BOOKING-${bookingId ?? "unknown"}`;
  return String(raw).replace(/[^\w.\-]+/g, "_").slice(0, 80);
}

const TABLE_OPTS = {
  theme: "plain",
  styles: { fontSize: 8.5, cellPadding: 1.6, textColor: [15, 23, 42], lineColor: BRAND_LIGHT, lineWidth: 0.1 },
  columnStyles: {
    0: { fontStyle: "bold", cellWidth: 44, textColor: BRAND },
    1: { cellWidth: "auto" },
  },
  margin: { left: MARGIN, right: MARGIN },
};

/**
 * Build a TripTrip-branded hotel booking voucher PDF from
 * POST /api/Hotel/booking/complete-details `data` payload.
 */
export async function generateAndSaveBookingVoucherPdf(data) {
  if (!data || typeof data !== "object") {
    throw new Error("No booking data for PDF");
  }

  const { order, booking, guests } = data;

  let logoDataUrl = null;
  try {
    logoDataUrl = await loadWordmarkPngDataUrl();
  } catch {
    logoDataUrl = null;
  }

  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const contentW = PAGE_W - MARGIN * 2;
  let y = MARGIN;

  const headerLogoWidth = 32;
  const headerLogoHeight = (646.57 / 3342.73) * headerLogoWidth;

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "PNG", MARGIN, y, headerLogoWidth, headerLogoHeight);
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BRAND);
    doc.text("TripTrip", MARGIN, y + 6);
    doc.setTextColor(0, 0, 0);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND);
  doc.text("Booking Voucher", PAGE_W - MARGIN, y + 6, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  const genLine = `Generated ${formatDateTime(new Date().toISOString())}`;
  doc.text(genLine, PAGE_W - MARGIN, y + 11, { align: "right" });
  doc.setTextColor(0, 0, 0);

  y += logoDataUrl ? headerLogoHeight + 6 : 14;

  doc.setFillColor(...BRAND);
  doc.rect(MARGIN, y, contentW, 0.5, "F");
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...BRAND);
  const hotelTitle = displayText(order?.hotelName || booking?.hotelName);
  const titleLines = doc.splitTextToSize(hotelTitle, contentW - 28);
  doc.text(titleLines, MARGIN, y);
  y += titleLines.length * 6 + 2;

  const location = formatBookingLocation(order);
  const isPaid = String(order?.paymentStatus || "").toUpperCase() === "CHARGED";

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  doc.text(location, MARGIN, y);

  if (isPaid) {
    const paidW = 14;
    const paidH = 5;
    const paidX = PAGE_W - MARGIN - paidW;
    const paidY = y - 3.5;
    doc.setFillColor(...SUCCESS);
    doc.roundedRect(paidX, paidY, paidW, paidH, 1, 1, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text("Paid", paidX + paidW / 2, paidY + 3.5, { align: "center" });
  }

  y += 8;
  doc.setTextColor(0, 0, 0);

  const sectionHead = (label, startY) => {
    doc.setFillColor(...BRAND);
    doc.roundedRect(MARGIN, startY, contentW, 5.5, 1, 1, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(label, MARGIN + 2.5, startY + 3.8);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    return startY + 7.5;
  };

  const halfW = (contentW - 4) / 2;

  y = sectionHead("Order details", y);
  autoTable(doc, {
    startY: y,
    tableWidth: halfW,
    body: [
      ["Order number", displayText(order?.orderNumber)],
      ["Order date", formatDateTime(order?.orderDate)],
      ["Order status", displayText(order?.orderStatus)],
      ["Payment status", formatPaymentStatusDisplay(order?.paymentStatus)],
      ["Payment mode", displayText(order?.paymentMode)],
      ["Transaction ref", displayText(order?.transactionRef)],
      ["Payment date", formatDateTime(order?.paymentDate)],
      ["Total", formatMoney(order?.totalAmount, order?.currencyCode)],
      ["Guests / rooms", `${order?.noOfGuests ?? "—"} guests · ${order?.noOfRooms ?? "—"} rooms`],
      ["Check in date", formatDate(order?.checkInDate)],
      ["Check out date", formatDate(order?.checkOutDate)],
    ],
    ...TABLE_OPTS,
    margin: { left: MARGIN, right: PAGE_W - MARGIN - halfW },
  });
  const orderEndY = doc.lastAutoTable.finalY;

  const resStartY = y - 7.5;
  y = sectionHead("Booking Information", resStartY);
  autoTable(doc, {
    startY: y,
    tableWidth: halfW,
    body: [
      ["Booking ID", displayText(booking?.supplierBookingId)],
      ["Room type", displayText(booking?.roomType)],
      ["Rate plan", displayText(booking?.ratePlan)],
      ["Nights", booking?.numberOfNights != null ? String(booking.numberOfNights) : "—"],
      ["Base", formatMoney(booking?.baseAmount, booking?.currencyCode)],
      ["Tax", formatMoney(booking?.taxAmount, booking?.currencyCode)],
      ["Total", formatMoney(booking?.totalAmount, booking?.currencyCode)],
      ["Booked on", formatDateTime(booking?.createdDate)],
    ],
    ...TABLE_OPTS,
    margin: { left: MARGIN + halfW + 4, right: MARGIN },
  });
  y = Math.max(orderEndY, doc.lastAutoTable.finalY) + 8;

  if (y > 230) {
    doc.addPage();
    y = MARGIN;
  }

  y = sectionHead("Guests", y);
  const guestRows = (guests || []).length
    ? guests.map((g) => [
        `${displayText(g.fullName)}${g.isLeadGuest ? " (Lead)" : ""}`,
        displayText(g.guestType),
        [displayText(g.email), g.isdCode ? `+${g.isdCode} ${displayText(g.contactNumber)}` : displayText(g.contactNumber)]
          .filter((x) => x && x !== "—")
          .join("\n"),
        displayText(g.panCardNumber),
        [displayText(g.passportNumber), g.passportExpiry ? `exp ${formatDate(g.passportExpiry)}` : ""]
          .filter(Boolean)
          .join("\n"),
      ])
    : [["No guest records", "", "", "", ""]];

  autoTable(doc, {
    startY: y,
    head: [["Guest", "Type", "Contact", "PAN", "Passport"]],
    body: guestRows,
    theme: "grid",
    headStyles: {
      fillColor: BRAND,
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 2,
    },
    styles: { fontSize: 8, cellPadding: 1.8, valign: "top", lineColor: BRAND_LIGHT, lineWidth: 0.1 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 38 },
      1: { cellWidth: 22 },
      2: { cellWidth: 52 },
      3: { cellWidth: 28 },
      4: { cellWidth: 38 },
    },
    margin: { left: MARGIN, right: MARGIN },
  });
  y = doc.lastAutoTable.finalY + 8;

  const cancelRules = parseCancellationPolicies(booking?.cancellationPolicy);
  if (y > 215) {
    doc.addPage();
    y = MARGIN;
  }

  y = sectionHead("Cancellation policy", y);
  if (cancelRules.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [["From", "Until", "Charge"]],
      body: cancelRules.map((r) => [
        formatDateTime(r.start),
        formatDateTime(r.end),
        r.valueType === "Percentage"
          ? `${r.value}%`
          : formatMoney(r.value ?? r.estimatedValue, booking?.currencyCode),
      ]),
      theme: "grid",
      headStyles: { fillColor: BRAND, textColor: 255, fontStyle: "bold", fontSize: 8, cellPadding: 2 },
      styles: { fontSize: 8, cellPadding: 1.6, lineColor: BRAND_LIGHT, lineWidth: 0.1 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 48 },
        2: { cellWidth: "auto" },
      },
      margin: { left: MARGIN, right: MARGIN },
    });
  } else {
    autoTable(doc, {
      startY: y,
      body: [["No structured cancellation rules for this booking."]],
      theme: "plain",
      styles: { fontSize: 8.5, cellPadding: 1.6, textColor: MUTED },
      margin: { left: MARGIN, right: MARGIN },
    });
  }
  y = doc.lastAutoTable.finalY + 10;

  const footY = Math.min(y, 285);
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, footY, PAGE_W - MARGIN, footY);
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  const footer = doc.splitTextToSize(
    "TripTrip — This document is a summary of your reservation as recorded in our system. For support, contact your account administrator. " +
      "Rates and policies are subject to supplier terms.",
    contentW
  );
  doc.text(footer, MARGIN, footY + 4);
  doc.setTextColor(0, 0, 0);

  const fname = `TripTrip_Booking_${safeFileName(order?.orderNumber, booking?.bookingId)}.pdf`;
  doc.save(fname);
}
